import { useCallback, useState } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { Modal } from '@simplywallst/ui-core';

import { useGeneratePortfolioLinkUrl } from '../../hooks/useGeneratePortfolioLinkUrl';

import type { Brokerage } from '../../hooks/useBrokerages';

const CreatePortfolioProviderLink = loadable(
  () => import('../CreatePortfolioProviderLink')
);

type Provider = {
  provider: 'passiv' | 'sharesight';
  portfolioId: string;
  redirectUrl: string;
};

const isValidProvider = (data?: {
  provider: string;
  portfolioId: string;
  redirectUrl: string;
}): data is Provider => {
  if (typeof data === 'undefined') return false;
  return data.provider === 'passiv' || data.provider === 'sharesight';
};

interface Props {
  brokerage: Brokerage;
  onSuccess: ({ portfolioId }: { portfolioId: string }) => void;
  onError: () => void;
  onProviderError?(errorStatusCode: number): void;
  onClose: () => void;
}

export const GeneratePortfolioLinkUrl = ({
  brokerage,
  onSuccess,
  onError,
  onProviderError,
  onClose,
}: Props) => {
  const { trackEvent } = useTracking({
    type: 'track',
  });

  const [showModal, setShowModal] = useState(false);

  const { status, data, remove } = useGeneratePortfolioLinkUrl({
    brokerageId: brokerage.id,
    onSuccess() {
      setShowModal(true);
      trackEvent({ action: 'success' });
    },
    onError,
  });

  const handleClose = useCallback(() => {
    setShowModal(false);
    remove();
    onClose();
  }, [remove, onClose]);

  const handleSuccess = useCallback(() => {
    data && onSuccess({ portfolioId: data.portfolioId });
    remove();
  }, [remove, onSuccess, data]);

  const isLoading = status === 'loading' || status === 'success';

  return (
    <>
      {showModal && isValidProvider(data) ? (
        <CreatePortfolioProviderLink
          data={data}
          onClose={handleClose}
          onError={onProviderError}
          onSuccess={handleSuccess}
        />
      ) : null}
      {isLoading ? <Modal isNaked isLoading /> : null}
    </>
  );
};
